import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, ChevronDown, ChevronRight, Lightbulb, Maximize2, Minimize2, Play, Route, X } from 'lucide-react';
import SegmentedToggle from '../SegmentedToggle';
import ONBOARDING, { TOPICS } from '../../data/onboarding';
import SAVOIRS from '../../data/savoirs';
import LEXICON from '../../data/lexicon';
import { helpForPage } from '../../data/pageHelp';
import { moduleForPage } from '../../nav/entitlements';
import { pageLabel } from '../../nav/pageLabel';
import { setDone, useProgress } from '../../data/progress';
import { guide, useGuide } from '../guide/guideStore';
import { normalize, notionText } from './SavoirSections';
import FicheIcon from './ficheIcons';
import './KnowledgeDrawer.css';

const WIDTH_KEY = 'palestre_kd_width';
const MIN_W = 320;
const MAX_W = 820;

/* ── Le PANNEAU Savoirs (2026-09-16) ────────────────────────────────────
   L'ampoule du rail n'ouvre plus un panneau de menu qui menait à trois pages
   portant chacune leur propre menu : elle ouvre ce panneau, ANCRÉ à droite,
   qui pousse la page au lieu de la recouvrir et fait pendant au menu de
   gauche (même surface). Une poignée sur son bord le redimensionne, largeur
   mémorisée ; un bouton l'étend sur tout le contenu. Trois vues en bascule,
   Guide · Fiches · Lexique, une seule liste sous chacune : un sujet se
   déplie sur place, un terme tient dans sa ligne, une fiche s'ouvre dans sa
   FENÊTRE (FicheWindow), la largeur d'une page pour la lire. Chaque vue
   commence par « Sur cette page ». « Me montrer » allume les repères et lit
   le geste sur l'écran, le panneau restant ouvert à côté. */
export default function KnowledgeDrawer({ open, onClose, page, nav, onOpenFiche }) {
  const { t, i18n } = useTranslation();
  const showEn = String(i18n.language || 'fr').startsWith('en');
  const [view, setView] = useState('guide');
  const [openTopic, setOpenTopic] = useState(null);
  const [qFiche, setQFiche] = useState('');
  const [qTerm, setQTerm] = useState('');
  const [openTerm, setOpenTerm] = useState(null);
  const [max, setMax] = useState(false);
  const [width, setWidth] = useState(() => {
    try { const v = Number(window.localStorage.getItem(WIDTH_KEY)); return v >= MIN_W && v <= MAX_W ? v : 380; } catch { return 380; }
  });
  const drag = useRef(null);
  const done = useProgress();
  const g = useGuide();
  /* Un parcours entièrement coché s'ouvre REPLIÉ sur sa ligne d'entête
     (étiquette, compte, barre) : il ne prend plus de place, il se rouvre d'un
     clic. Le repli se décide à l'ouverture du panneau, pas sous la souris. */
  /* Le repli a un DÉFAUT (le module où l'on est est ouvert, les autres sont
     fermés, un guide entièrement fait se ferme) et le clic le BASCULE. Les
     basculements se perdent au changement de module : on ne traîne pas d'un
     écran à l'autre un repli décidé ailleurs. */
  const [flipped, setFlipped] = useState(() => new Set());
  const toggleGroup = (key) => setFlipped((prev) => {
    const next = new Set(prev);
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  });
  const help = useMemo(() => helpForPage(page), [page]);
  /* Les trois hooks du rangement se déclarent ICI, avec les autres : plus bas,
     ils passaient après un retour anticipé, donc conditionnellement. */
  const moduleId = moduleForPage(page);
  const pageTopics = useMemo(() => new Set(help.topics.map((tp) => tp.id)), [help]);
  const moduleGuides = useMemo(() => (nav || [])
    .filter((n) => n.type === 'band')
    .map((n) => ONBOARDING.find((gd) => gd.module === n.id))
    .filter(Boolean), [nav]);
  /* Les basculements de repli se perdent au changement de module : on ne traîne
     pas d'un écran à l'autre un repli décidé ailleurs. */
  useEffect(() => { setFlipped(new Set()); }, [moduleId]);
  const title = pageLabel(nav, page, t);

  /* Poignée : on tire le bord gauche, la largeur suit la souris et se retient. */
  const onGrip = useCallback((e) => {
    e.preventDefault();
    drag.current = { x: e.clientX, w: width };
    const move = (ev) => {
      if (!drag.current) return;
      const next = Math.max(MIN_W, Math.min(MAX_W, drag.current.w + (drag.current.x - ev.clientX)));
      setWidth(next);
    };
    const up = () => {
      drag.current = null;
      document.removeEventListener('mousemove', move);
      document.removeEventListener('mouseup', up);
      setWidth((w) => { try { window.localStorage.setItem(WIDTH_KEY, String(w)); } catch { /* quota */ } return w; });
    };
    document.addEventListener('mousemove', move);
    document.addEventListener('mouseup', up);
  }, [width]);

  /* Un sujet démarre (depuis le panneau ou depuis un repère) : sa ligne se
     déplie sur le pas-à-pas et vient en vue. Le curseur reste ici : on lit
     la page, on avance depuis le panneau. */
  useEffect(() => {
    if (!g.topic) return;
    setView('guide');
    setOpenTopic(g.topic);
    const el = document.querySelector(`[data-topic="${g.topic}"]`);
    if (el) el.scrollIntoView({ block: 'nearest' });
  }, [g.topic]);

  useEffect(() => {
    if (!open) return undefined;
    const esc = (e) => { if (e.key === 'Escape' && !e.defaultPrevented) onClose(); };
    document.addEventListener('keydown', esc);
    return () => document.removeEventListener('keydown', esc);
  }, [open, onClose]);

  if (!open) return null;

  /* ── Guide ─────────────────────────────────────────────────────────── */
  const topicRow = (tp) => {
    const isDone = !!done[tp.id];
    const running = g.topic === tp.id;
    const isOpen = openTopic === tp.id || running;
    const canShow = tp.steps.some((s) => s.target);
    const last = g.step >= tp.steps.length - 1;
    const n = tp.steps.length;
    return (
      <div key={tp.id} data-topic={tp.id}
           className={`kd-topic${isOpen ? ' is-open' : ''}${isDone ? ' is-done' : ''}${running ? ' is-running' : ''}`}>
        <div className="kd-row" role="button" tabIndex={0}
             onClick={() => setOpenTopic(isOpen ? null : tp.id)}
             onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setOpenTopic(isOpen ? null : tp.id); } }}>
          {/* DEUX niveaux (2026-09-16) : l'intitulé, puis sa nature et son
              état dessous. Sur une ligne, le titre, le nombre d'étapes et la
              flèche se disputaient la largeur. Un sujet est un REPÈRE (une
              bulle sur un élément) ou une MARCHE À SUIVRE (n étapes). */}
          {/* La coche OUVRE la ligne (retour au 2026-09-16, après un passage à
              droite le même jour) : posée devant l'intitulé, elle aligne les
              sujets sur une colonne et les distingue d'un coup d'œil. À
              droite, avec le chevron, deux ronds voisins se confondaient et
              rien ne séparait plus un sujet du suivant. Le clic reste arrêté
              sur elle, la ligne s'ouvre partout ailleurs. */}
          <span className="kd-check" role="checkbox" aria-checked={isDone}
                aria-label={`${t('common:onboarding.markDone')} — ${tp.title}`}
                onClick={(e) => { e.stopPropagation(); setDone(tp.id, !isDone); }}>
            {isDone && <Check size={10} strokeWidth={3} aria-hidden="true" />}
          </span>
          <span className="kd-row-text">
            <span className="kd-row-title">
              {tp.title}
              {/* CE QUI SE JOUE ICI : le repère remplace la section « Sur cette
                  page », qui sortait le sujet de la liste de son module. */}
              {pageTopics.has(tp.id) && (
                <span className="kd-here">{t('common:guide.here', 'sur cet écran')}</span>
              )}
            </span>
            <span className="kd-meta">
              {n > 1 ? t('common:guide.steps', { count: n }) : t('common:guide.oneStep')}
              {' · '}
              {isDone ? t('common:guide.doneShort') : t('common:guide.todoShort')}
            </span>
          </span>
          <ChevronRight size={15} strokeWidth={1.8} className="kd-chev" aria-hidden="true" />
        </div>
        {isOpen && (
          <div className="kd-topic-body">
            {/* Une ligne d'état PUIS la rangée de boutons, dans les deux états,
                à cases de même largeur : « Me montrer » et « Suivant » occupent
                la même place, la souris ne bouge pas quand le sujet démarre. */}
            {running && (
              <div className="kd-status">{t('common:guide.stepOf', { index: g.step + 1, total: n })}</div>
            )}
            <div className="kd-stepper">
              {running ? (
                <>
                  <button type="button" className="kd-btn kd-btn--fixed" disabled={g.step === 0} onClick={() => guide.goto(g.step - 1)}>
                    ‹ {t('common:onboarding.coach.prev')}
                  </button>
                  <button type="button" className="kd-btn kd-btn--primary kd-btn--fixed"
                          onClick={() => { if (last) { setDone(tp.id, true); guide.end(); } else guide.goto(g.step + 1); }}>
                    {last ? t('common:onboarding.coach.finish') : `${t('common:onboarding.coach.next')} ›`}
                  </button>
                </>
              ) : (
                <>
                  <button type="button" className={`kd-btn kd-btn--fixed${isDone ? ' is-done' : ''}`}
                          onClick={() => setDone(tp.id, !isDone)}>
                    {isDone ? `✓ ${t('common:onboarding.done')}` : t('common:onboarding.markDone')}
                  </button>
                  {canShow && (
                    <button type="button" className="kd-btn kd-btn--primary kd-btn--fixed" onClick={() => guide.start(tp.id)}>
                      <Play size={12} strokeWidth={2} aria-hidden="true" /> {t('common:onboarding.showMe')}
                    </button>
                  )}
                </>
              )}
            </div>
            {/* Rien d'autre que les commandes : le texte vit sur l'écran, dans
                la bulle. Une phrase de résumé sous chaque sujet noyait la
                liste et répétait ce que l'intitulé dit déjà (2026-09-16). */}
            {running && (
              <div className="kd-keys-row">
                <span className="kd-keys">{t('common:guide.keys')}</span>
                <span className="kd-keys"> · </span>
                <button type="button" className="kd-link" onClick={() => guide.end()}>{t('common:onboarding.coach.quit')}</button>
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  /* UN MODULE, UNE LISTE, UN ORDRE FIXE (2026-09-18). Le Guide rangeait ses
     marches à suivre en « Sur cette page » puis « Ailleurs dans {module} » :
     un même module se coupait donc en deux, et ses sujets changeaient de bloc
     selon l'écran ouvert. Désormais L'ESSENTIEL en tête, le module où l'on est
     juste après et ouvert, les autres dessous et fermés, dans l'ordre du menu.
     Seul change celui qui est ouvert. Ce qui concerne l'écran ouvert se
     REPÈRE sur sa ligne, dans la liste de son module, au lieu d'en être
     extrait. Un module que le client n'a pas ne se liste pas : `nav` arrive
     déjà filtré par ses droits. */
  const hereGuide = moduleGuides.find((gd) => gd.module === moduleId) || null;
  const otherGuides = moduleGuides.filter((gd) => gd !== hereGuide);
  /* Une SECTION : un titre discret en petites capitales, son compte, un
     chevron. Le contexte (le client, la page) vit déjà en tête du panneau :
     un gros titre « Guides · Rapports » le répétait, et le nom du module
     coincé entre le titre et le compteur n'apprenait rien. */
  const section = (key, label, list, { secondary = false, rowsOf = null, shut = false } = {}) => {
    if (!list.length) return null;
    const n = list.filter((tp) => done[tp.id]).length;
    const total = list.length;
    const complete = n === total;
    const parDefaut = shut || complete;
    const isCollapsed = flipped.has(key) ? !parDefaut : parDefaut;
    return (
      <section key={key} className={`kd-grp${isCollapsed ? ' is-collapsed' : ''}${secondary ? ' kd-grp--secondary' : ''}`}>
        <button type="button" className="kd-grp-head" onClick={() => toggleGroup(key)} aria-expanded={!isCollapsed}>
          <span className="kd-grp-title">{label}</span>
          <span className="kd-count">
            {complete ? `✓ ${t('common:guide.allDone', { count: total })}` : t('common:guide.doneCount', { n, total })}
          </span>
          {isCollapsed
            ? <ChevronRight size={15} strokeWidth={1.8} className="kd-grp-chev" aria-hidden="true" />
            : <ChevronDown size={15} strokeWidth={1.8} className="kd-grp-chev" aria-hidden="true" />}
        </button>
        {!isCollapsed && (rowsOf || list).map((tp) => topicRow(TOPICS.find((x) => x.id === tp.id) || tp))}
      </section>
    );
  };
  const essential = ONBOARDING.find((gd) => !gd.module);
  const guideView = (
    <>
      <h2 className="kd-h2">{t('common:guide.heading')}</h2>
      <p className="kd-lead">{t('common:guide.lead')}</p>
      {essential && section(essential.id, essential.label, essential.topics, { secondary: true })}
      {hereGuide && section(hereGuide.id, hereGuide.label, hereGuide.topics)}
      {otherGuides.map((gd) => section(gd.id, gd.label, gd.topics, { shut: true }))}
    </>
  );

  /* ── Fiches ────────────────────────────────────────────────────────── */
  const needle = normalize(qFiche).trim();
  const ficheRow = (f) => (
    <button key={f.id} type="button" className="kd-row kd-row--btn" onClick={() => onOpenFiche && onOpenFiche(f.id)}>
      <FicheIcon fiche={f} />
      <span className="kd-row-title">{f.title}</span>
      <span className="kd-cat">{f.category}</span>
    </button>
  );
  const groupHead = (label) => <div className="kd-group-head"><span>{label}</span></div>;
  /* « Sur cette page » porte le MÊME costume dans les trois vues : une carte
     blanche à étiquette navy, quand le reste de la liste est à plat. */
  const pageCard = (children) => (
    <section className="kd-grp kd-grp--page">
      <div className="kd-grp-head">
        <span className="kd-tag">{t('common:knowledge.onThisPage')}{title ? ` · ${title}` : ''}</span>
      </div>
      {children}
    </section>
  );
  const hits = needle ? SAVOIRS.filter((f) => notionText(f).includes(needle)) : null;
  const fichesView = (
    <>
      <h2 className="kd-h2">{t('common:knowledge.fichesHeading')}</h2>
      <p className="kd-lead">{t('common:knowledge.fichesLead')}</p>
      <input type="search" className="kd-search" placeholder={t('common:knowledge.searchFiches')}
             value={qFiche} onChange={(e) => setQFiche(e.target.value)} aria-label={t('common:knowledge.searchFiches')} />
      {hits ? (
        <section className="kd-group">
          {hits.map(ficheRow)}
          {hits.length === 0 && <p className="kd-empty">{t('common:knowledge.noResult')}</p>}
        </section>
      ) : (
        <>
          {help.fiches.length > 0 && pageCard(help.fiches.map(ficheRow))}
          <section className="kd-group">
            {groupHead(t('common:knowledge.allFiches'))}
            {SAVOIRS.map(ficheRow)}
          </section>
        </>
      )}
    </>
  );

  /* ── Lexique ───────────────────────────────────────────────────────── */
  const tn = normalize(qTerm).trim();
  /* UNE LIGNE par terme dans la liste complète (2026-09-16) : soixante
     définitions ouvertes d'affilée noyaient les quelques termes que l'écran
     ouvert emploie. La définition reste visible là où elle se lit — « Sur
     cette page » et les résultats d'une recherche — et se déplie au clic
     ailleurs, un terme à la fois. C'est ce que le panneau annonce déjà :
     un terme, une ligne. */
  const termRow = (e, full = false) => {
    const head = (
      <div className="kd-term-head">
        <span className="kd-term-fr">{e.fr}</span>
        {showEn && <span className="kd-term-en">{e.en}</span>}
        <span className="kd-cat">{e.module}</span>
      </div>
    );
    if (full) {
      return (
        <div key={e.fr} className="kd-term">
          {head}
          <p className="kd-term-note">{e.note}</p>
        </div>
      );
    }
    const on = openTerm === e.fr;
    return (
      <div key={e.fr} className="kd-term">
        <button type="button" className="kd-term-btn" aria-expanded={on}
                onClick={() => setOpenTerm(on ? null : e.fr)}>
          {head}
        </button>
        {on && <p className="kd-term-note">{e.note}</p>}
      </div>
    );
  };
  const termMatch = (e) => normalize(e.fr).includes(tn) || (showEn && normalize(e.en).includes(tn))
    || normalize(e.module).includes(tn) || normalize(e.note).includes(tn);
  const termHits = tn ? LEXICON.filter(termMatch) : null;
  const lexView = (
    <>
      <h2 className="kd-h2">{t('common:knowledge.lexiconHeading')}</h2>
      <p className="kd-lead">{t('common:knowledge.lexiconLead')}</p>
      <input type="search" className="kd-search" placeholder={t('common:knowledge.searchTerms')}
             value={qTerm} onChange={(e) => setQTerm(e.target.value)} aria-label={t('common:knowledge.searchTerms')} />
      {termHits ? (
        <section className="kd-group">
          {termHits.map((e) => termRow(e, true))}
          {termHits.length === 0 && <p className="kd-empty">{t('common:knowledge.noResult')}</p>}
        </section>
      ) : (
        <>
          {help.terms.length > 0 && pageCard(help.terms.map((e) => termRow(e, true)))}
          <section className="kd-group">
            {groupHead(t('common:knowledge.allTerms'))}
            {LEXICON.map(termRow)}
          </section>
        </>
      )}
    </>
  );

  return (
    <aside className={`kd${max ? ' kd--max' : ''}`} style={max ? undefined : { width }}
           role="complementary" aria-label={t('common:knowledge.title')}>
      {!max && (
        <div className="kd-grip" onMouseDown={onGrip} role="separator" aria-orientation="vertical"
             title={t('common:knowledge.resize')} aria-label={t('common:knowledge.resize')} />
      )}
      <header className="kd-head">
        <Lightbulb size={17} strokeWidth={1.7} className="kd-head-ic" aria-hidden="true" />
        <span className="kd-title">{t('common:knowledge.title')}</span>
        {title && <span className="kd-page">· {title}</span>}
        <button type="button" className="kd-icon" onClick={() => setMax((v) => !v)}
                title={max ? t('common:knowledge.restore') : t('common:knowledge.maximize')}
                aria-label={max ? t('common:knowledge.restore') : t('common:knowledge.maximize')} aria-pressed={max}>
          {max ? <Minimize2 size={15} strokeWidth={1.8} aria-hidden="true" /> : <Maximize2 size={15} strokeWidth={1.8} aria-hidden="true" />}
        </button>
        <button type="button" className="kd-icon" onClick={onClose}
                title={t('common:actions.close')} aria-label={t('common:actions.close')}>
          <X size={16} strokeWidth={1.8} aria-hidden="true" />
        </button>
      </header>
      <div className="kd-seg">
        <SegmentedToggle compact value={view} onChange={setView}
          options={[
            { value: 'guide', label: <><Route size={13} strokeWidth={1.9} aria-hidden="true" /> {t('common:knowledge.guide')}</> },
            { value: 'fiches', label: t('common:knowledge.fiches') },
            { value: 'lexique', label: t('common:knowledge.lexicon') },
          ]} />
      </div>
      <div className="kd-body">
        {view === 'guide' && guideView}
        {view === 'fiches' && fichesView}
        {view === 'lexique' && lexView}
      </div>
    </aside>
  );
}
